import { useMemo } from 'react';
import { AFFILIATE_MENUITEMS, MENUITEMS } from '@/lib/constants';
import { useUser } from '@/queries/useUser';
import { useAuthStore } from '@/stores';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';

export function useMenuBar() {
    const accountScopesInclude = useAuthStore(state => state.accountScopesInclude);
    const currentAccountKey = useAuthStore(state => state.currentAccountKey);
    const { user } = useUser();

    const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;

    const topMenuItems = useMemo(() => {
        // Affiliate-only users get a limited menu
        if (isAffiliateOnlySubscription) {
            // AFFILIATE_MENUITEMS includes AFFILIATE, INCOME, SETTINGS
            // For top menu, show only AFFILIATE and INCOME (SETTINGS goes to bottom)
            return AFFILIATE_MENUITEMS.filter(item => item.id !== 'settings' && accountScopesInclude(item.scopes));
        }

        const topFixedMenuItems = MENUITEMS.top.fixed;
        const topOptionalMenuItems = MENUITEMS.top.optional;
        const pinnedNavigationItems = user?.data?.pinned_navigation ?? [];
        const pinnedIds = new Set(pinnedNavigationItems.map(item => item.id));
        const out = topFixedMenuItems.concat(topOptionalMenuItems.filter(item => pinnedIds.has(item.id)));
        return out.filter(item => accountScopesInclude(item.scopes));
    }, [
        user?.data?.pinned_navigation,
        isAffiliateOnlySubscription,
        accountScopesInclude,
        currentAccountKey, // Needed to trigger re-render when account changes
    ]);

    const bottomMenuItems = useMemo(() => {
        // Affiliate-only users only see Settings in bottom menu
        if (isAffiliateOnlySubscription) {
            return AFFILIATE_MENUITEMS.filter(item => item.id === 'settings' && accountScopesInclude(item.scopes));
        }

        const bottomFixedMenuItems = MENUITEMS.bottom.fixed;
        return bottomFixedMenuItems.filter(item => accountScopesInclude(item.scopes));
    }, [isAffiliateOnlySubscription, accountScopesInclude, currentAccountKey]);

    return { topMenuItems, bottomMenuItems, isAffiliateOnlySubscription };
}
